import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Calendar, MapPin, Users, Linkedin, Instagram, Youtube } from "lucide-react";
import PageHero from "../components/sections/PageHero";
import { fetchPrograms } from "../lib/api";

const CATEGORIES = [
  "Tech Programs",
  "Non-Tech Programs",
  "Employability Programs",
  "Cohort-Based Programs",
];

const WHATSAPP_REGISTER = (title) =>
  `https://wa.me/918122431120?text=${encodeURIComponent(
    `Hi Deployedd Labs — I'd like to know more about "${title}".`
  )}`;

const Pulse = () => {
  const [programs, setPrograms] = useState([]);
  const [active, setActive] = useState(CATEGORIES[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrograms()
      .then(setPrograms)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(CATEGORIES.map((c) => [c, []]));
    programs.forEach((p) => {
      if (map[p.category]) map[p.category].push(p);
      else map["Tech Programs"].push(p);
    });
    return map;
  }, [programs]);

  const visible = grouped[active] || [];

  return (
    <div data-testid="pulse-page">
      <PageHero
        kicker="/ Pulse — Live signal"
        title="Upcoming programs."
        accent="Field signal."
        breadcrumb={["Pulse"]}
        lead="What's planned, what's open for partner conversations, and where Deployedd Labs is showing up next — across our four program tracks."
      />

      {/* Upcoming Programs */}
      <section data-testid="upcoming-programs" className="container-wide py-24 md:py-32">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A] mb-6">/ 01 — Upcoming programs</p>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">
              Four tracks. <em className="font-editorial italic text-[#9F381A]">One operating logic.</em>
            </h2>
          </div>
          <p className="text-sm text-ink-muted max-w-sm">
            All upcoming programs are planned post May 2026 across our Chennai and Coimbatore campuses + select partner institutions.
          </p>
        </div>

        {/* Category tabs */}
        <div data-testid="program-tabs" className="flex flex-wrap gap-2 border-t border-b border-ink py-4 mb-12">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`tab-${cat.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setActive(cat)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                active === cat
                  ? "bg-ink text-white border-ink"
                  : "border-transparent text-ink-muted hover:border-ink-strong hover:text-ink"
              }`}
            >
              {cat} <span className="ml-2 text-[10px] opacity-70">{(grouped[cat] || []).length}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p data-testid="pulse-loading" className="font-mono-cust text-sm text-gray-500">Loading…</p>
        ) : visible.length === 0 ? (
          <div data-testid="program-empty" className="border-t border-ink py-16 text-center">
            <p className="font-editorial italic text-2xl text-ink-muted">More programs in this track will be announced soon.</p>
          </div>
        ) : (
          <div data-testid="program-list" className="border-t border-ink">
            {visible.map((p, i) => (
              <article
                key={p.slug}
                data-testid={`program-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-8 md:py-10 border-b border-ink group hover:bg-[#F3F3F1] transition-colors px-2"
              >
                <div className="md:col-span-1">
                  <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A]">
                    /{String(i + 1).padStart(2, "0")}
                  </p>
                </div>
                <div className="md:col-span-3">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    />
                  </div>
                </div>
                <div className="md:col-span-5">
                  <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-3">{p.kicker}</p>
                  <h3 className="font-heading text-2xl md:text-3xl tracking-tight leading-tight mb-3 group-hover:translate-x-1 transition-transform">
                    {p.title}
                  </h3>
                  <p className="text-base text-ink-muted leading-relaxed">{p.description}</p>
                </div>
                <div className="md:col-span-3 flex flex-col gap-3 text-sm">
                  <span className="inline-flex items-center gap-2 text-ink"><Calendar className="w-4 h-4 text-[#9F381A]" /> {p.starts_on}</span>
                  <span className="inline-flex items-center gap-2 text-ink"><MapPin className="w-4 h-4 text-[#9F381A]" /> {p.location}</span>
                  <span className="inline-flex items-center gap-2 text-ink"><Users className="w-4 h-4 text-[#9F381A]" /> {p.audience}</span>
                  <a
                    href={WHATSAPP_REGISTER(p.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`program-register-${i}`}
                    className="mt-1 inline-flex items-center justify-between gap-2 bg-ink text-white px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors"
                  >
                    Initiate Discussion · WhatsApp <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Social Buzz — coming soon */}
      <section data-testid="social-buzz" className="bg-ink text-white py-24 md:py-32">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-6">/ 02 — Social buzz</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-8">
              Our social presence will be<br />
              <em className="font-editorial italic text-[#E68A6E]">initiated soon.</em>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-xl">
              We're prepping a dedicated editorial line — long-form notes from the bench, behind-the-scenes from cohorts, and field signal from partner campuses. Follow us so you don't miss the first post.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">Follow along</p>
            <div className="grid grid-cols-3 gap-3">
              <a
                href="https://www.linkedin.com/company/deployedd-labs/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                aria-label="Deployedd Labs on LinkedIn"
                className="aspect-square border border-white/30 flex items-center justify-center hover:bg-white hover:text-ink hover:border-white transition-colors group"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="Deployedd Labs on Instagram"
                className="aspect-square border border-white/30 flex items-center justify-center hover:bg-white hover:text-ink hover:border-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-youtube"
                aria-label="Deployedd Labs on YouTube"
                className="aspect-square border border-white/30 flex items-center justify-center hover:bg-white hover:text-ink hover:border-white transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
            <p className="mt-6 font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white/40">
              First post — soon
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-wide py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">
            Want a program designed for<br />
            <em className="font-editorial italic text-[#9F381A]">your campus or company?</em>
          </h2>
        </div>
        <div className="lg:col-span-4">
          <a
            href="https://wa.me/918122431120?text=Hi%20Deployedd%20Labs%20%E2%80%94%20I%27d%20like%20to%20design%20a%20program."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pulse-cta"
            className="inline-flex items-center justify-between w-full md:w-auto gap-3 bg-ink text-white px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors"
          >
            Initiate Discussion · WhatsApp <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Pulse;
